<script setup lang="ts">
    import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
    import { computed } from 'vue';

    interface Props {
        currentPage: number;
        totalPages: number;
        maxVisiblePages?: number;
    }

    const props = withDefaults(defineProps<Props>(), {
        maxVisiblePages: 5,
    });

    const emit = defineEmits<{
        (e: 'pageChange', page: number): void;
    }>();

    const visiblePages = computed(() => {
        const pages: (number | string)[] = [];
        const halfVisible = Math.floor(props.maxVisiblePages / 2);
        let startPage = Math.max(1, props.currentPage - halfVisible);
        let endPage = Math.min(props.totalPages, startPage + props.maxVisiblePages - 1);

        if (endPage - startPage + 1 < props.maxVisiblePages) {
            startPage = Math.max(1, endPage - props.maxVisiblePages + 1);
        }

        if (startPage > 1) {
            pages.push(1);
            if (startPage > 2) {
                pages.push('...');
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < props.totalPages) {
            if (endPage < props.totalPages - 1) {
                pages.push('...');
            }
            pages.push(props.totalPages);
        }

        return pages;
    });

    const handlePageClick = (page: number | string) => {
        if (typeof page === 'number' && page !== props.currentPage) {
            emit('pageChange', page);
        }
    };

    const handlePrevious = () => {
        if (props.currentPage > 1) {
            emit('pageChange', props.currentPage - 1);
        }
    };

    const handleNext = () => {
        if (props.currentPage < props.totalPages) {
            emit('pageChange', props.currentPage + 1);
        }
    };
</script>

<template>
    <div class="flex items-center justify-center gap-2">
        <!-- Previous Button -->
        <base-button
            @click="handlePrevious"
            :disabled="currentPage === 1"
            fill="outline"
            class="h-10 w-10 p-0"
        >
            <ChevronLeft class="size-5" />
        </base-button>

        <!-- Page Numbers -->
        <base-button
            v-for="(page, index) in visiblePages"
            :key="index"
            @click="handlePageClick(page)"
            :disabled="page === '...'"
            :fill="page === currentPage ? 'solid' : 'outline'"
            :class="[
                'h-10 min-w-10 px-3',
                page === '...' &&
                    'cursor-default border-transparent bg-transparent hover:bg-transparent',
            ]"
        >
            {{ page }}
        </base-button>

        <!-- Next Button -->
        <base-button
            @click="handleNext"
            :disabled="currentPage === totalPages"
            fill="outline"
            class="h-10 w-10 p-0"
        >
            <ChevronRight class="size-5" />
        </base-button>
    </div>
</template>
